import { useMemo, useState, type JSX } from 'react';
import type { CSSProperties } from 'react';
import { useIsomorphicLayoutEffect } from 'ahooks';
import type { CarouselNavigationItemProps } from '@archipro-design/aria';
import { pxToRem, useTheme } from '@archipro-design/aria';
import { Button, CarouselNavigation, useStyles } from '@archipro-design/aria';

import * as S from './ProductGalleryThumbs.style';
import { ArrowLeftSLine, ArrowRightSLine } from '@archipro-design/icons';
import galleryThumbTransition from '../../util/gallery-thumb-transition';

import { PRODUCT_THUMB_GAP, PRODUCT_THUMB_SIZE } from './ProductGallery.style';

interface ProductGalleryProps {
    navigation: {
        id: number;
        thumbnail: JSX.Element;
    }[];
    activeGallerySlide: number;
    swipeInitialised: boolean;
    slideToIndex: (index: number, speed: number) => void;
}

/*
    Calculates the amount of tabs each group should have.
    The first and last groups will have 5 tabs, otherwise the groups inbetween
    will have 4 tabs with prev and next buttons respectively. i.e.
    [
        [1, 2, 3, 4, 5],
        [6, 7, 8, 9],
        [10, 11, 12, 13, 14]
    ]
*/

const getThumbGroupCount = (thumbs: number) => {
    const thumbCountArray = Array.from(Array(thumbs).keys());

    return [
        thumbCountArray.splice(0, 5),
        ...thumbCountArray.reduce((acc, _, __, arr) => {
            if (thumbCountArray.length > 5) {
                acc.push(arr.splice(0, 4));
            }
            return acc;
        }, [] as number[][]),
        thumbCountArray.splice(0, 5),
    ];
};

const ProductGalleryThumbs = ({
    navigation,
    activeGallerySlide,
    swipeInitialised,
    slideToIndex,
}: ProductGalleryProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [currentTabGroup, setCurrentTabGroup] = useState(1);

    const THUMB_WIDTH = PRODUCT_THUMB_SIZE + PRODUCT_THUMB_GAP;
    const slideCount = navigation.length;
    const thumbGroups = getThumbGroupCount(slideCount);
    const totalTabGroups = thumbGroups.length;

    const showPrevBtn = currentTabGroup > 1;
    const showNextBtn = currentTabGroup !== totalTabGroups && slideCount > 6;

    useIsomorphicLayoutEffect(() => {
        setActiveIndex(activeGallerySlide);

        if (slideCount === 6) return;

        // Navigate to next thumb group if next thumb index is inside that group
        thumbGroups.some((group, idx) => {
            if (
                group.includes(activeGallerySlide) &&
                currentTabGroup !== idx + 1
            ) {
                setCurrentTabGroup(idx + 1);
                return true;
            }

            return false;
        });
    }, [activeGallerySlide]);

    const translateWidth = galleryThumbTransition(
        THUMB_WIDTH,
        slideCount,
        currentTabGroup,
        totalTabGroups,
        thumbGroups,
    );

    const { css } = useStyles({
        THUMB_WIDTH,
        slideCount,
        showPrevBtn,
        showNextBtn,
        translateWidth,
        swipeInitialised,
    });
    const { siteVariables } = useTheme();

    const navigationItems = useMemo(
        () => ({
            'aria-label': 'Product gallery navigation',
            items: navigation.map((item, index) => ({
                key: `thumbnail-slide-${item.id}`,
                'aria-controls': item.id,
                style: {
                    '--thumb-ring-color':
                        index === activeIndex
                            ? siteVariables.colors.charcoal[250]
                            : 'transparent',
                } as CSSProperties,
                content: item.thumbnail,
            })),
        }),
        [activeIndex, navigation, siteVariables.colors.charcoal],
    );

    return (
        <div className={css(S.ThumbsContainer)}>
            <Button
                className={css(S.LeftNavBtn)}
                transparent
                onClick={() => setCurrentTabGroup((prev) => prev - 1)}
                icon={<ArrowLeftSLine />}
            />

            <div className={css(S.ThumbList)}>
                {CarouselNavigation.create(navigationItems, {
                    generateKey: true,
                    defaultProps: () => ({
                        className: css(S.ThumbSlider),
                        maxTabs: 5,
                        activeTabIndex: activeIndex,
                        design: {
                            width: pxToRem(slideCount * THUMB_WIDTH),
                        },
                    }),
                    overrideProps: () => ({
                        onItemClick: (
                            _,
                            itemProps: CarouselNavigationItemProps,
                        ) => {
                            const { index } = itemProps;
                            if (typeof index === 'number') {
                                slideToIndex(index, 400);
                                setActiveIndex(index);
                            }
                        },
                    }),
                })}
            </div>
            <Button
                className={css(S.RightNavBtn)}
                transparent
                onClick={() => setCurrentTabGroup((prev) => prev + 1)}
                icon={<ArrowRightSLine />}
            />
        </div>
    );
};

export default ProductGalleryThumbs;
